/**
 * Spike 4 — Class 2 structural isolation scaffold (§5.1).
 *
 * Per-plugin iframe on a distinct origin with postMessage broker-not-bearer.
 * Not wired into the shell loader until Class 2 is explicitly enabled.
 */

export type BrokerMessage =
  | { type: 'PLUGIN_READY'; pluginId: string; nonce: string }
  | { type: 'EXECUTE'; opRefId: string; params: Record<string, unknown>; nonce: string }
  | { type: 'EXECUTE_RESULT'; nonce: string; data: unknown }
  | { type: 'EXECUTE_ERROR'; nonce: string; message: string };

export interface Class2BrokerOptions {
  pluginId: string;
  previewOrigin: string;
  shellOrigin: string;
  onExecute: (opRefId: string, params: Record<string, unknown>) => Promise<unknown>;
}

/**
 * Host-side broker: validates origin + nonce before proxying execute calls.
 * Tokens never enter the iframe — broker-not-bearer.
 */
export class Class2PluginBroker {
  private readonly pending = new Set<string>();

  constructor(private readonly opts: Class2BrokerOptions) {}

  handleMessage(event: MessageEvent, iframe: HTMLIFrameElement): void {
    if (event.origin !== this.opts.previewOrigin) return;
    const data = event.data as BrokerMessage;
    if (!data || typeof data !== 'object') return;

    if (data.type === 'PLUGIN_READY') {
      if (data.pluginId !== this.opts.pluginId) return;
      this.pending.add(data.nonce);
      return;
    }

    if (data.type === 'EXECUTE') {
      if (!this.pending.has(data.nonce)) return;
      void this.opts.onExecute(data.opRefId, data.params)
        .then((result) => {
          iframe.contentWindow?.postMessage(
            { type: 'EXECUTE_RESULT', nonce: data.nonce, data: result } satisfies BrokerMessage,
            this.opts.previewOrigin,
          );
        })
        .catch((err: Error) => {
          iframe.contentWindow?.postMessage(
            { type: 'EXECUTE_ERROR', nonce: data.nonce, message: err.message } satisfies BrokerMessage,
            this.opts.previewOrigin,
          );
        });
    }
  }
}

export function isClass2IsolationEnabled(): boolean {
  return (import.meta as { env?: { VITE_PLUGIN_CLASS2_ENABLED?: string } }).env?.VITE_PLUGIN_CLASS2_ENABLED === 'true';
}

export function getClass2SandboxOrigin(): string {
  const env = (import.meta as { env?: { VITE_PLUGIN_CLASS2_ORIGIN?: string } }).env;
  return (env?.VITE_PLUGIN_CLASS2_ORIGIN || window.location.origin).replace(/\/$/, '');
}

export function class2SandboxBundleUrl(pluginId: string, version: string): string {
  return `${getClass2SandboxOrigin()}/api/plugins/plugin-sandbox/${pluginId}/${version}`;
}

export function class2SandboxFrameUrl(pluginId: string, version: string): string {
  return `${getClass2SandboxOrigin()}/api/plugins/plugin-sandbox-frame/${pluginId}/${version}`;
}
